import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts';
import { formatCurrency, formatPercentage } from '../../../utils/CurrencyFormat';
import {
  ASSET_PROGRESS_SERIES,
  NetWorthSnapshot,
  buildAssetProgressRows,
  seriesChangeStats,
} from '../../../models/NetWorthTypes';
import { EmptyState, SectionPanel } from '../../../shared-components/ui';

type ViewMode = 'grid' | 'combined';

function formatAxisInr(v: number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(0)}k`;
  return `₹${n.toFixed(0)}`;
}

function ChangeChip({ pct }: { pct: number | null }) {
  if (pct == null || !Number.isFinite(pct)) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const up = pct >= 0;
  return (
    <Chip
      size="small"
      label={`${up ? '+' : ''}${formatPercentage(pct)}`}
      color={up ? 'success' : 'error'}
      variant="outlined"
      sx={{ height: 22, fontWeight: 600 }}
    />
  );
}

interface AssetProgressChartsProps {
  snapshotsAsc: NetWorthSnapshot[];
}

const AssetProgressCharts: React.FC<AssetProgressChartsProps> = ({ snapshotsAsc }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [hideEmpty, setHideEmpty] = useState(true);

  const rows = useMemo(() => buildAssetProgressRows(snapshotsAsc), [snapshotsAsc]);

  const activeSeries = useMemo(() => {
    if (!hideEmpty) return ASSET_PROGRESS_SERIES;
    return ASSET_PROGRESS_SERIES.filter((s) => snapshotsAsc.some((snap) => s.value(snap) > 0));
  }, [snapshotsAsc, hideEmpty]);

  if (snapshotsAsc.length < 2) {
    return (
      <SectionPanel
        title="Asset progress"
        subtitle="Track how each bucket moves across snapshots"
      >
        <EmptyState
          title="Need at least two snapshots"
          description="Add another month’s snapshot to unlock per-asset progress graphs."
        />
      </SectionPanel>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <SectionPanel
        title="Asset progress"
        subtitle="INR value over time for each net-worth attribute"
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideEmpty}
                  onChange={(_, v) => setHideEmpty(v)}
                />
              }
              label={<Typography variant="body2">Hide empty</Typography>}
              sx={{ mr: 0 }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, v: ViewMode | null) => {
                if (v) setViewMode(v);
              }}
            >
              <ToggleButton value="grid">Per asset</ToggleButton>
              <ToggleButton value="combined">Combined</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      >
        {activeSeries.length === 0 ? (
          <EmptyState title="No asset values yet" description="Fill bucket amounts on snapshots to see progress." />
        ) : viewMode === 'combined' ? (
          <Box sx={{ height: 420, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={28} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatAxisInr} width={56} />
                <ReTooltip
                  formatter={(v: number, name: string) => [formatCurrency(v, 'INR', true), name]}
                  labelFormatter={(d) => `As of ${d}`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {activeSeries.map((s) => (
                  <Line
                    key={s.id}
                    type="monotone"
                    dataKey={s.id}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {activeSeries.map((series) => {
              const stats = seriesChangeStats(snapshotsAsc, series);
              return (
                <Grid item xs={12} sm={6} lg={4} key={series.id}>
                  <Box
                    sx={{
                      p: 1.75,
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: series.color, lineHeight: 1.3 }}
                        >
                          {series.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                          {formatCurrency(stats.latest, 'INR', true)}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          MoM
                        </Typography>
                        <ChangeChip pct={stats.momPct} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Since start
                        </Typography>
                        <ChangeChip pct={stats.sinceStartPct} />
                      </Stack>
                    </Stack>
                    <Box sx={{ height: 160, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={rows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id={`fill-${series.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={series.color} stopOpacity={0.35} />
                              <stop offset="100%" stopColor={series.color} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                          <XAxis dataKey="date" hide />
                          <YAxis hide domain={['auto', 'auto']} />
                          <ReTooltip
                            formatter={(v: number) => [formatCurrency(v, 'INR', true), series.label]}
                            labelFormatter={(d) => `As of ${d}`}
                          />
                          <Area
                            type="monotone"
                            dataKey={series.id}
                            stroke={series.color}
                            strokeWidth={2}
                            fill={`url(#fill-${series.id})`}
                            dot={false}
                            connectNulls
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </SectionPanel>
    </Box>
  );
};

export default AssetProgressCharts;
